/**
 * @file   src\Pages\EditLocation.tsx
 * @brief  EditLocation page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, useOutletContext, Container, Button, Row, Col, useNavigate, Tab, Tabs, useRef, useParams, useLocation, Breadcrumb } from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWRadio from '../components/CWRadio';
import CWSelect from '../components/CWSelect';
import CWSwitch from '../components/CWSwitch';
import { pageURLs } from '../utils/pageUrls';
import { CITY_MAX_LENGTH, EMAIL_MAX_LENGTH, LOCATION_NAME_MAX_LENGTH, TIMEZONE_OFFSET_OPTIONS, WASH_ADDRESS_MAX_LENGTH } from '../utils/constants';
import { PaymentType, FleetPaymentMode, userType } from '../utils/enums';
import { bankingInfoSchema } from '../validations/bankingInfoSchema';
import * as Yup from 'yup';
import { LocationData, BankDatanew, AppContextType } from '../interfaces/generalInterface';
import { locationInfoSchema } from '../validations/locationInfoSchema';
import { Option } from "../interfaces/stateInterface";
import { CountryItem } from "../interfaces/CarWashInterface";
import axios from '../config/axiosConfig';
import { CompanyApi, LocationApi } from "../utils/apiUrls";
import { fetchStatesByCountry, fetchSupportedCountries, getCountryCodeFromRecord, getCountryDisplayName, getCountryFieldLabels, getPostalCodeMaxLength, getSelectedOptions, isUnitedStates, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import { MessageToaster } from "../utils/toastUtil";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SmallLoader from '../components/SmallLoader';
import CWOEditLocationBankInformation from './CWOEditLocationBankInformation';
import { hasInvalidStripeSecretKey, STRIPE_SECRET_KEY_LIVE_ERROR, stripePublicKeyCwInputLabel, stripeSecretKeyCwInputLabel } from '../components/stripePaymentInputLabels';
import LocationFleetSettings from '../components/LocationFleetSettings';
import LocationFeatureToggles from '../components/LocationFeatureToggles';
import { DEFAULT_LOCATION_FLEET_SETTINGS, getDisabledFleetSettings, getLocationFleetRequestFields, parseLocationFleetSettings } from '../utils/locationFleetSettings';
import { DEFAULT_LOCATION_FEATURE_TOGGLES, getLocationFeatureRequestFields, parseLocationFeatureToggles } from '../utils/locationFeatureSettings';

const stripePromise = loadStripe(process.env.REACT_APP_SUPERADMIN_STRIPE_PUBLIC_KEY || "");


const EditLocation = (props: any) => {

    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('locationinfo');
    const param = useParams();
    const location_id = param.id ? parseInt(param.id) : 0;
    const navigate = useNavigate();
    const toaster = new MessageToaster();
    const location = useLocation();
    const page = location.state?.page || 1;
    const state = location.state || {};
    const { current_carwash_id } = state;

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    // To display heading
    useEffect(() => {
        handleUpdate()
    }, [])
    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Edit Location", url: props.userType === userType['CARWASH-OWNER'] ? `/${pageURLs.carwash_locations}` : `/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`, showBackButton: true, showMainMenu: true, activeLink: props.userType === userType['CARWASH-OWNER'] ? "manage-car-wash-locations" : "manage-car-wash" });
    };

    // State to company bank details
    const [companybank, setCompanybank] = useState ({
        PaymentMethod: 1,
        stripeSecretKey: "",
        stripePublicKey: "",
    }
    );

    // To get company bank details
    const getCompanybank = async () => {
        try {
            let param = '';
            param += current_carwash_id;
            const { data } = (props.userType !== userType['CARWASH-OWNER']) ? 
            await axios.get(CompanyApi.GET_COMPANY_BANK_DETAILS + param) :
            await axios.get(CompanyApi.CARWASH_OWNER_GET_COMPANY_BANK_DETAILS)
            if (data?.error === 0) {
                const paymentDetails = data.data.paymentDetails;
                if (paymentDetails && paymentDetails !== "") {
                    setCompanybank({
                        PaymentMethod: data?.data?.PaymentMethod,
                        stripeSecretKey: paymentDetails?.stripeSecretKey,
                        stripePublicKey: paymentDetails?.stripePublicKey
                    });
                }
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // To get company bank details
    const getLocationbank = async () => {
        try {
            let param = '';
            param += location_id;
            const { data } = (props.userType !== userType['CARWASH-OWNER']) ?  
            await axios.get(LocationApi.GET_LOCATION_BANK_DETAILS + param)
            : await axios.get(LocationApi.CARWASH_OWNER_GET_LOCATION_BANK_DETAILS+ param)
            if (data?.error === 0) {
                const paymentDetails = data.data.paymentDetails;
                if (paymentDetails && paymentDetails !== "") {
                    setBankFormData({
                        account:  data?.data?.HasDefaultAccount === 0 ? false : true,
                        PaymentMethod:  data?.data?.PaymentMethod,
                        stripeSecretKey: paymentDetails?.stripeSecretKey,
                        stripePublicKey: paymentDetails?.stripePublicKey
                    });
                }
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    useEffect(() => {
       
            getCompanybank(); // company bank
            getLocationbank(); //location bank
        
    }, []);

    // State to handle options for country and state
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [options, setOptions] = useState<Option[]>([]);
    // State to company details
    const [company, setCompany] = useState( {
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zipCode: ""
    } );

    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries);
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    }

    const getSateList = async (countryCode: string) => {
        if (!countryCode) {
            setOptions([]);
            return;
        }
        try {
            const stateOptions = await fetchStatesByCountry(countryCode);
            setOptions(stateOptions);
        } catch (error: any) {
            setOptions([]);
            toaster.toastError(error.message);
        }
    }

    // To get company details
    const getCompany = async () => {
        try {
            let param = '';
            param += current_carwash_id;
            const { data } =  (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + param) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
            if (data?.error === 0) {
                const countryCode = getCountryCodeFromRecord(data.data, { fallback: false });
                const companyData = {
                    ...data.data,
                    country: countryCode,
                };
                setCompany(companyData);
                return companyData;
            }
            return null;

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    //State to handle form data
    const [formData, setFormData] = useState<LocationData>( {
        carWashId: parseInt(current_carwash_id),
        locationName: '',
        email: '',
        timezoneOffset: '',
        isComboWash: false,
        ...DEFAULT_LOCATION_FLEET_SETTINGS,
        ...DEFAULT_LOCATION_FEATURE_TOGGLES,
        hasDefaultAddress: true,
        address1: '',
        address2: '',
        city: '',
        country: '',
        state: '',
        zipcode: ''
    } );
    const countryOptions = mapCountryOptions(supportedCountries);
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, formData.country);
    const postalMaxLength = getPostalCodeMaxLength(formData.country);
    const companyCountryName = getCountryDisplayName(company.country);
    const showAchBankStep = props.userType === userType['CARWASH-OWNER'] && isUnitedStates(company.country);

    // To get list of bays in location
    const getLocation = async (defaultCountry = '') => {
        try {
            let param = '';
            param += location_id;
            const { data } = props.userType !== userType['CARWASH-OWNER'] ? await axios.get(LocationApi.GET_SINGLE_LOCATION + param) : await axios.get(LocationApi.CARWASH_OWNER_LOCATION_DETAILS+ param);
            const countryCode = defaultCountry || getCountryCodeFromRecord(data?.data, { fallback: false });
            setFormData({
                ...data?.data,
                hasDefaultAddress: (data?.data?.hasDefaultAddress === 1 ? true : false),
                isComboWash: data?.data?.comboWash === 1 || data?.data?.comboWash === true || data?.data?.comboWash === "1",
                ...parseLocationFleetSettings(data?.data),
                ...parseLocationFeatureToggles(data?.data),
                country: countryCode,
                zipcode: data?.data?.zipCode,
                timezoneOffset: data?.data?.offset !== undefined && data?.data?.offset !== null
                    ? data?.data?.offset.toString()
                    : (data?.data?.timezoneOffset !== undefined && data?.data?.timezoneOffset !== null
                        ? data?.data?.timezoneOffset.toString()
                        : '')
            });

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    useEffect(() => {
        getCountries();
    }, []);

    useEffect(() => {
        const loadCompanyCountry = async () => {
            setLoading(true);
            try {
                const companyData = await getCompany();
                const countryCode = companyData?.country || '';
                if (countryCode) {
                    await getSateList(countryCode);
                }
                await getLocation(countryCode);
            } finally {
                setLoading(false);
            }
        };
        loadCompanyCountry();
    }, [location_id]);

    useEffect(() => {
        if (!showAchBankStep && selectedTab === 'paymentinfo') {
            setSelectedTab('locationinfo');
        }
    }, [showAchBankStep, selectedTab]);

    // State to handle location errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        if ((e.target.name === "zipcode")) {
            e.target.value = normalizePostalCodeInput(e.target.value, formData.country);
        }

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
        let newFormData
        if(name=="locationName")
        {
             newFormData = {
                ...formData,
                [name]: value,
            };  
        }
        else{
             newFormData = {
                ...formData,
                [name]: value.trim(),
            };
        }
        

        setFormData(newFormData);

    };


    //To validate form on clicking next
    const locationvalidateForm = async () => {
        try {
            setLoading(true); // Show loader
            await locationInfoSchema.validate(formData, { abortEarly: false });
            const locationRequestBody = {
                locationId: location_id,
                locationName: formData?.locationName.trim(),
                email: formData?.email,
                offset: formData?.timezoneOffset !== undefined ? Number(formData?.timezoneOffset) : undefined,
                isComboWash: Boolean(formData?.isComboWash),
                ...getLocationFleetRequestFields(formData),
                ...getLocationFeatureRequestFields(formData),
                hasDefaultAddress: formData?.hasDefaultAddress,
                address1: formData?.address1,
                address2: formData?.address2,
                city: formData?.city,
                countryCode: formData?.country,
                stateCode: formData?.state,
                zip: formData?.zipcode,
            };
            try {
                const responseLocationUpdate = (props.userType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.UPDATE_LOCATION, locationRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_UPDATE_LOCATION, locationRequestBody);
                if (responseLocationUpdate?.data?.error === 0) {
                    setErrors({
                        carWashId: parseInt(current_carwash_id),
                        locationName: '',
                        email: '',
                        timezoneOffset: '',
                        hasDefaultAddress: '',
                        address1: '',
                        address2: '',
                        city: '',
                        country: '',
                        state: '',
                        zipcode: ''
                    });
                    if (props.userType !== userType['CARWASH-OWNER']) {
                        navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`);
                    } else {
                        navigate(`/${pageURLs.carwash_locations}?page=${page}`);
                    }
                }
                else {
                    toaster.toastError(responseLocationUpdate?.data?.message);
                }
            }
            catch (error) {
                console.log(error);
                toaster.toastError('Failed to update location.');
            }
        } catch (error) {
            const newErrors: any = {};
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

            // Focus on the first field with an error
            if (Object.keys(newErrors).length > 0) {
                const firstErrorField = Object.keys(newErrors)[0];
                const element = refs.current[firstErrorField];
                if (element) {
                    element.focus();
                }
            }
            return false
        } finally {
            setLoading(false); // Hide loader
        }
    };

    const onChangeAction = (value: boolean) => {
        if (value) {
            const companyCountry = company.country || '';
            setFormData({
                ...formData,
                hasDefaultAddress: true,
                country: companyCountry,
                state: company.state || '',
            });
            if (companyCountry) {
                getSateList(companyCountry);
            }
            return;
        }
        setFormData({
            ...formData,
            hasDefaultAddress: false,
        });
    };

    const handleComboWashChange = (value: boolean) => {
        setFormData({
            ...formData,
            isComboWash: value,
        });
    };

    const handleFeatureToggle = (field: 'allowGiftCardManagement' | 'blockDiscountedGiftCards' | 'allowLoyaltyPointsManagement', value: boolean) => {
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    const clearFleetFieldErrors = (...fieldNames: string[]) => {
        const hasFieldError = fieldNames.some((fieldName) => errors[fieldName]);
        if (!hasFieldError) {
            return;
        }
        const nextErrors = { ...errors };
        fieldNames.forEach((fieldName) => {
            nextErrors[fieldName] = '';
        });
        setErrors(nextErrors);
    };

    const handleFleetDiscountToggle = (enabled: boolean) => {
        setFormData({
            ...formData,
            ...(enabled
                ? { enableFleetDiscount: true, fleetPaymentMode: FleetPaymentMode.CreditCard }
                : getDisabledFleetSettings()),
        });
        clearFleetFieldErrors(
            'enableFleetDiscount',
            'fleetDiscount',
            'fleetPaymentMode'
        );
    };

    const handleFleetDiscountChange = (value: string) => {
        setFormData({
            ...formData,
            fleetDiscount: value,
        });
        clearFleetFieldErrors('fleetDiscount');
    };

    const handleFleetPaymentModeChange = (mode: number) => {
        setFormData({
            ...formData,
            fleetPaymentMode: mode,
        });
        clearFleetFieldErrors('fleetPaymentMode');
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
        if (name === 'country') {
            if (!value || value === formData.country) {
                return;
            }
            setErrors({
                ...errors,
                country: '',
                state: '',
                zipcode: '',
            });
            setFormData({
                ...formData,
                country: value,
                state: '',
                zipcode: '',
            });
            getSateList(value);
            return;
        }
        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
    };


    // start bank update functions

    // const [bankvaluechangecheck, setBankvaluechangecheck] = useState(false);

    // State to handle errors of bank
    const [errorsbank, setErrorsbank] = useState<any>({});

    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];

    //State to handle form data
    const [bankformData, setBankFormData] = useState<BankDatanew>({
        account: true,
        PaymentMethod: 1,
        stripeSecretKey: "",
        stripePublicKey: ""
    }
    );

    // On input change
    const bankhandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // setBankvaluechangecheck(true);

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errorsbank);
        if (keys.includes(name)) {
            setErrorsbank({
                ...errorsbank,
                [name]: ""
            })
        }

        const newFormData = {
            ...bankformData,
            [name]: value.trim(),
        };

        setBankFormData(newFormData);
    };

    //To validate form on clicking next
    const bankvalidateForm = async (): Promise<boolean> => {
        if (!bankformData.account && bankformData.PaymentMethod === 1 && hasInvalidStripeSecretKey(bankformData.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return false;
        }

        try {
           
            setLoading(true); // Show loader
            await bankingInfoSchema.validate(bankformData, { abortEarly: false });
                const onwerlocbankingData = bankformData?.account === false ? {
                    locationId: location_id,
                    PaymentMethod : bankformData?.PaymentMethod,
                    HasDefaultAccount: 0,
                    paymentKeys:{
                        stripeSecretKey: bankformData?.stripeSecretKey,
                        stripePublicKey: bankformData?.stripePublicKey
                    }
                } :
                {
                    locationId: location_id,
                    HasDefaultAccount: 1,
                } ;
                const adminlocbankingData = bankformData?.account === false ? {
                    locationId: location_id,
                    carwashid: current_carwash_id,
                    PaymentMethod : bankformData?.PaymentMethod,
                    HasDefaultAccount: 0,
                    paymentKeys:{
                        stripeSecretKey: bankformData?.stripeSecretKey.trim(),
                        stripePublicKey: bankformData?.stripePublicKey.trim()
                    }
                } :
                {
                    locationId: location_id,
                    carwashid: current_carwash_id,
                    HasDefaultAccount: 1,
                } ;
                //Validating payment method key
                if(bankformData?.account === false) {
                    setLoading(true)
                    const paymentData = {
                        "PaymentMethod" : bankformData.PaymentMethod,
                        "paymentKeys" : onwerlocbankingData.paymentKeys
                    }
                        const { data } = (props.userType !== userType['CARWASH-OWNER']) ?
                        await axios.post(CompanyApi.VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData) :
                        await axios.post(CompanyApi.CARWASH_OWNER_VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData ) ;
                       
                        if (data?.error !== 0) {
                            setLoading(false)
                            toaster.toastError(data?.message);
                            return false;
                        }
                }
                setLoading(true)
                const responseLocationBankUpdate = (props.userType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.UPDATE_LOCATION_PAYMENT_METHOD, adminlocbankingData) : await axios.post(LocationApi.CARWASH_OWNER_UPDATE_LOCATION_PAYMENT_METHOD, onwerlocbankingData);
                setLoading(false)
                if (responseLocationBankUpdate?.data?.error === 0) {
                    toaster.toastSuccess(responseLocationBankUpdate?.data?.message)
                    if (props.userType !== userType['CARWASH-OWNER']) {
                        navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`);
                    } else {
                        navigate(`/${pageURLs.carwash_locations}?page=${page}`);
                    }
                } else {
                    toaster.toastError(responseLocationBankUpdate?.data?.message)
                }   

                setErrorsbank({});
                return true;
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrorsbank(newErrors);
            }
            return false
        } finally {
            setLoading(false); // Hide loader
        }

    };



    // Triggers whenever options is selected
    const bankhandleSelectChange = (selectedOption: any, name: string) => {
        // setBankvaluechangecheck(true);
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        const keys = Object.keys(errorsbank);
        if (keys.includes(name)) {
            setErrorsbank({
                ...errorsbank,
                [name]: ""
            })
        }

        const newFormData = {
            ...bankformData,
            [name]: value,
        };
        setBankFormData(newFormData);
    };

    const bankonChangeAction = (value: boolean) => {
        // setBankvaluechangecheck(true);

        const newFormData = {
            ...bankformData,
            'account': value,
        };

        setBankFormData(newFormData);
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }



    return (
        <Elements stripe={stripePromise}>
            <div className='manage-subscription-container'>
                <Container>
                    <Breadcrumb>
                        {props.userType !== userType['CARWASH-OWNER'] ?
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            : null}
                        <Breadcrumb.Item 
                            onClick={() => {
                                if (props.userType !== userType['CARWASH-OWNER']) {
                                    itemClick(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`);
                                } else {
                                    itemClick(`/${pageURLs.carwash_locations}`);
                                }
                            }}
                        >
                            {props.userType !== userType['CARWASH-OWNER'] ? company.companyName : "Manage Car Wash Locations"}
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active>{formData.locationName}</Breadcrumb.Item>
                    </Breadcrumb>
                    <div className='cw-form-outer-wrap'>
                        <div className="cw-main-tab">
                            <Tabs
                                activeKey={selectedTab}
                                id="profile-tab"
                                className="cw-tab-title justify-content-end"
                                onSelect={(k) => setSelectedTab(k || 'locationinfo')}
                            >
                                <Tab eventKey="locationinfo" title="Location Info">
                                    <div className="cw-form-container location-tab-form">
                                        <div className="form-inner mx-auto">
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="locationName"
                                                        name="locationName"
                                                        type="text"
                                                        label="Location Name"
                                                        value={formData.locationName}
                                                        onChange={handleChange}
                                                        errorMessage={errors.locationName}
                                                        maxLength={LOCATION_NAME_MAX_LENGTH}
                                                        isMandatory
                                                        ref={(el) => (refs.current.locationname = el)}
                                                        autoFocus
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="email"
                                                        name="email"
                                                        type="text"
                                                        label="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        errorMessage={errors.email}
                                                        maxLength={EMAIL_MAX_LENGTH}
                                                        ref={(el) => (refs.current.email = el)}
                                                        isMandatory
                                                    />
                                                </Col>
                                            </Row>
                                            <CWSelect
                                                id="timezoneOffset"
                                                name="timezoneOffset"
                                                options={TIMEZONE_OFFSET_OPTIONS}
                                                values={TIMEZONE_OFFSET_OPTIONS.filter(option => option.value === formData.timezoneOffset)}
                                                value={formData.timezoneOffset}
                                                label="Timezone"
                                                dropdownGap={0}
                                                dropdownPosition="auto"
                                                placeholder="Select"
                                                multi={false}
                                                searchable={true}
                                                isMandatory
                                                errorMessage={errors.timezoneOffset}
                                                onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'timezoneOffset')}
                                                className="no-input"
                                            />
                                            <div className="car-info-sec d-flex flex-column align-items-start mb-4">
                                                <div className="info-switch d-flex align-items-center gap-3">
                                                    <h5 className="f-bold m-0">Allow Combo Washes</h5>
                                                    <CWSwitch
                                                        id="isComboWash"
                                                        name="isComboWash"
                                                        checked={formData.isComboWash}
                                                        onChange={() => handleComboWashChange(!formData.isComboWash)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="adderss-sec">
                                                <h5 className='f-bold mt-3'>Address</h5>
                                                <div className="radio-field-group d-flex">
                                                    <CWRadio
                                                        id="address1"
                                                        name="hasDefaultAddress"
                                                        label="Use Car Wash Address"
                                                        checked={formData.hasDefaultAddress}
                                                        onChange={() => onChangeAction(true)}

                                                    />
                                                    <CWRadio
                                                        id="newaddress"
                                                        name="hasDefaultAddress"
                                                        label="Add/Edit Address"
                                                        checked={!formData.hasDefaultAddress}
                                                        onChange={() => onChangeAction(false)}
                                                    />
                                                </div>

                                                {formData.hasDefaultAddress ?
                                                    (<div className="address mb-4">
                                                        <p className='f-medium'>
                                                            {company.companyName && `${company.companyName}, `}
                                                            {company.address1 && `${company.address1}`}<br />
                                                            {company.city && `${company.city}, `}
                                                            {company.state && `${company.state} `}
                                                            {company.zipCode && company.zipCode}
                                                            {companyCountryName && `${company.city || company.state || company.zipCode ? ', ' : ''}${companyCountryName}`}
                                                        </p>
                                                    </div>
                                                    ) : null
                                                }
                                                {((!formData.hasDefaultAddress)) ? (

                                                    <>
                                                        <Row>
                                                            <Col md={6}>
                                                                <CWInput
                                                                    id="address1"
                                                                    name="address1"
                                                                    type="text"
                                                                    value={formData.address1}
                                                                    label="Car Wash Address"
                                                                    onChange={handleChange}
                                                                    errorMessage={errors.address1}
                                                                    maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                                    ref={(el) => (refs.current.washaddress = el)}
                                                                    isMandatory
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <CWInput
                                                                    id="address"
                                                                    name="address2"
                                                                    value={formData.address2}
                                                                    maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                                    type="text"
                                                                    label="Address 2"
                                                                    labelSuffix="(Optional)"
                                                                    errorMessage={errors.address2}
                                                                    onChange={handleChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col md={6}>
                                                                <CWInput
                                                                    id="city"
                                                                    name="city"
                                                                    type="text"
                                                                    label="City"
                                                                    value={formData.city}
                                                                    maxLength={CITY_MAX_LENGTH}
                                                                    isMandatory
                                                                    ref={(el) => (refs.current.city = el)}
                                                                    errorMessage={errors.city}
                                                                    onChange={handleChange}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <CWSelect
                                                                    id="country"
                                                                    name="country"
                                                                    options={countryOptions}
                                                                    values={getSelectedOptions(countryOptions, formData.country)}
                                                                    label="Country"
                                                                    dropdownGap={0}
                                                                    dropdownPosition="auto"
                                                                    placeholder="Select"
                                                                    multi={false}
                                                                    searchable={false}
                                                                    disabled
                                                                    isMandatory
                                                                    errorMessage={errors.country}
                                                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'country')}
                                                                    className="no-input"
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col md={6}>
                                                                <CWSelect
                                                                    id="state"
                                                                    name="state"
                                                                    options={options}
                                                                    values={getSelectedOptions(options, formData.state)}
                                                                    value={formData.state}
                                                                    label={stateLabel}
                                                                    dropdownGap={0}
                                                                    dropdownPosition="auto"
                                                                    placeholder="Select"
                                                                    multi={false}
                                                                    searchable={true}
                                                                    isMandatory
                                                                    errorMessage={errors.state}
                                                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'state')}
                                                                    className="no-input"
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <CWInput
                                                                    id="zipcode"
                                                                    name="zipcode"
                                                                    type="text"
                                                                    value={formData.zipcode}
                                                                    label={postalLabel}
                                                                    isMandatory
                                                                    maxLength={postalMaxLength}
                                                                    errorMessage={errors.zipcode}
                                                                    ref={(el) => (refs.current.zipcode = el)}
                                                                    onChange={handleChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                ) : null}
                                            </div>
                                            <LocationFleetSettings
                                                enableFleetDiscount={Boolean(formData.enableFleetDiscount)}
                                                fleetDiscount={formData.fleetDiscount || ''}
                                                fleetPaymentMode={formData.fleetPaymentMode ?? null}
                                                errors={errors}
                                                onToggle={handleFleetDiscountToggle}
                                                onDiscountChange={handleFleetDiscountChange}
                                                onPaymentModeChange={handleFleetPaymentModeChange}
                                                refs={refs}
                                            />
                                            <LocationFeatureToggles
                                                allowGiftCardManagement={Boolean(formData.allowGiftCardManagement)}
                                                blockDiscountedGiftCards={Boolean(formData.blockDiscountedGiftCards)}
                                                allowLoyaltyPointsManagement={Boolean(formData.allowLoyaltyPointsManagement)}
                                                onGiftCardChange={(enabled) => handleFeatureToggle('allowGiftCardManagement', enabled)}
                                                onBlockDiscountedGiftCardsChange={(enabled) => handleFeatureToggle('blockDiscountedGiftCards', enabled)}
                                                onLoyaltyChange={(enabled) => handleFeatureToggle('allowLoyaltyPointsManagement', enabled)}
                                            />
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => props.userType !== userType['CARWASH-OWNER'] ? navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`)
                                                    : navigate(`/${pageURLs.carwash_locations}`)}>
                                                    Cancel
                                                </Button>
                                                <Button title="Save" variant="primary" className="primary-btn" onClick={() =>  locationvalidateForm() }>Save</Button>
                                            </div>
                                        </div>
                                    </div>
                                </Tab>
                                {showAchBankStep && (
                                    <Tab eventKey="paymentinfo" title="Bank Information">
                                        {selectedTab === 'paymentinfo' ? (
                                            <CWOEditLocationBankInformation locationId={location_id} email={formData.email} />
                                        ) : null}
                                    </Tab>
                                )}
                                <Tab eventKey="bankinginfo" title="Payment Method Info">
                                    <div className="cw-form-container location-tab-form">
                                        <div className="form-inner mx-auto">
                                            <div className="adderss-sec">
                                                <h5 className='f-bold'>Select Payment Type</h5>
                                                <div className="radio-field-group d-flex">
                                                    <CWRadio
                                                        id="washaccount"
                                                        name="account"
                                                        label="Use Car Wash Account"
                                                        checked={bankformData.account}
                                                        onChange={() => bankonChangeAction(true)}
                                                        disabled={!companybank.stripeSecretKey}
                                                    />
                                                    <CWRadio
                                                        id="newaccount"
                                                        name="account"
                                                        label="Add/Edit Account"
                                                        checked={!bankformData.account}
                                                        onChange={() => bankonChangeAction(false)}
                                                    />
                                                </div>
                                            </div>

                                            {!bankformData.account ? <div className="stripe-payment-note mb-5">
                                                <p>To accept real payments, you need to use your Stripe production keys. Make sure you're in the Stripe production dashboard. </p>
                                                <p>To switch to production mode(if you are in Test Mode):</p>
                                                <ul>
                                                    <li>1. Go to the left-hand menu.</li>
                                                    <li>2. Click on your account name.</li>
                                                    <li>3. A toggle popup will appear.</li>
                                                    <li>4. Click on "Exit test mode".</li>
                                                </ul>
                                                <p>Once you're in production mode, you can copy your live public key and secret key from the API keys section.</p>
                                                <p>To find your keys for copying and pasting to carwashapp.com</p>
                                                <ul>
                                                    <li>1.Log into your Stripe account</li>
                                                    <li>2.Click Developers, bottom left of screen</li>
                                                    <li>3.Go to API Keys section </li>
                                                </ul>
                                                <p>You’ll see two types of keys:</p>
                                                <ul>
                                                    <li>Publishable Key (Public) → Starts with pk_live_....</li>
                                                    <li>Secret Key (Private) → Starts with sk_live_....</li>
                                                </ul>
                                            </div> : ''}

                                            {!bankformData.account && (
                                                <>
                                                    <CWSelect
                                                        id="PaymentMethod"
                                                        name="PaymentMethod"
                                                        options={paymentOptions}
                                                        label="Select Merchant Payment Type"
                                                        errorMessage={errorsbank.PaymentMethod && errorsbank.PaymentMethod}
                                                        dropdownGap={0}
                                                        dropdownPosition="auto"
                                                        values={paymentOptions.filter(option => option.value === bankformData.PaymentMethod)}
                                                        onChange={(selectedOptions) => bankhandleSelectChange(selectedOptions[0], 'PaymentMethod')}
                                                        placeholder="Select Merchant Payment Type"
                                                        multi={false}
                                                        searchable={false}
                                                        isMandatory
                                                        className="no-input"
                                                    />
                                                    <h5 className='mb-4 f-bold'>Account Information</h5>
                                                    {bankformData.PaymentMethod === 1 && (
                                                    <Row>
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="stripeSecretKey"
                                                                name="stripeSecretKey"
                                                                type="text"
                                                                label={stripeSecretKeyCwInputLabel}
                                                                value={bankformData.stripeSecretKey}
                                                                onChange={bankhandleChange}
                                                                errorMessage={errorsbank.stripeSecretKey}
                                                                ref={(el) => (refs.current.stripeSecretKey = el)}
                                                                autoComplete="off"
                                                                isMandatory
                                                                autoFocus
                                                            />
                                                        </Col>
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="stripePublicKey"
                                                                name="stripePublicKey"
                                                                type="text"
                                                                label={stripePublicKeyCwInputLabel}
                                                                value={bankformData.stripePublicKey}
                                                                onChange={bankhandleChange}
                                                                errorMessage={errorsbank.stripePublicKey}
                                                                isMandatory
                                                                ref={(el) => (refs.current.stripePublicKey = el)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    )}
                                                    {bankformData.PaymentMethod === 2 && (
                                                    <Row>
                                                    {/* use when world pay is ready */}
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="SecretKey"
                                                                name="SecretKey"
                                                                type="text"
                                                                label="SecretKey"
                                                                onChange={bankhandleChange}
                                                                // value={bankformData.SecretKey}
                                                                // errorMessage={errorsbank.SecretKey}
                                                                // ref={(el) => (refs.current.SecretKey = el)}
                                                                autoComplete="off"
                                                                isMandatory
                                                                autoFocus
                                                            />
                                                        </Col>
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="PublicKey"
                                                                name="PublicKey"
                                                                type="text"
                                                                label="Account Name"
                                                                onChange={bankhandleChange}
                                                                // value={bankformData.PublicKey}
                                                                // errorMessage={errorsbank.PublicKey}
                                                                // ref={(el) => (refs.current.PublicKey = el)}
                                                                isMandatory
                                                            />
                                                        </Col>
                                                    </Row>
                                                    )}
                                                </>
                                            )}
                                            {bankformData.account && (
                                                <>
                                                    <CWInput
                                                        id="bankName"
                                                        name="bankName"
                                                        type="text"
                                                        label="Merchant Payment Type"
                                                        value={companybank.PaymentMethod === 1 ? "Stripe" : "WorldPay"}
                                                        isMandatory
                                                        ref={(el) => (refs.current.bankName = el)}
                                                        disabled
                                                    />
                                                    <h5 className='mb-4 f-bold'>Account Information </h5>
                                                    <Row>
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="stripeSecretKey"
                                                                name="stripeSecretKey"
                                                                type="text"
                                                                label="Stripe Secret Key"
                                                                value={companybank.stripeSecretKey}
                                                                autoComplete="off"
                                                                disabled
                                                            />
                                                        </Col>
                                                        <Col lg={12}>
                                                            <CWInput
                                                                id="stripePublicKey"
                                                                name="stripePublicKey"
                                                                type="text"
                                                                label="Stripe Public Key"
                                                                value={companybank.stripePublicKey}
                                                                autoComplete="off"
                                                                disabled
                                                            />
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                            {!bankformData.account && (
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => props.userType !== userType['CARWASH-OWNER'] ?
                                                    navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}?page=${page}`) : navigate(`/${pageURLs.carwash_locations}`)}>Cancel</Button>
                                                <Button title="Save" variant="primary" className="primary-btn" onClick={bankvalidateForm}>Save</Button>
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>

                </Container>
                {loading && <SmallLoader />}
            </div>
        </Elements>
    );
}

export default EditLocation;
